/**
 * File Suggestion Handler — artifact-aware @ completion for Claude Code.
 *
 * Reads stdin prefix, filters navigation-index artifacts, and emits NDJSON
 * candidates sorted by relevance score.
 */

import { listArtifacts, type NavigationArtifact } from "./artifact-resolver.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FileSuggestionCandidate {
  label: string;
  path: string;
  kind: "artifact" | "file";
  /** Artifact ID this file/pattern belongs to. */
  artifact?: string;
  /** Authority of the artifact (canonical, generated, etc.). */
  authority?: string;
  /** Manual edit policy for the artifact. */
  manual_edit?: string;
  /** Relevance score (higher = more relevant). */
  score: number;
}

export interface FileSuggestionOptions {
  /** The navigation index to search. */
  navigationIndex: Record<string, unknown>;
  /** Maximum number of candidates to return. */
  maxCandidates?: number;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

const DEFAULT_MAX_CANDIDATES = 100;

/**
 * Generate file suggestion candidates from the navigation index.
 * Filters by prefix if provided, then returns NDJSON-formatted candidates.
 */
export function generateCandidates(
  prefix: string,
  options: FileSuggestionOptions,
): FileSuggestionCandidate[] {
  const { navigationIndex, maxCandidates = DEFAULT_MAX_CANDIDATES } = options;
  const artifacts = listArtifacts(navigationIndex);
  const candidates: FileSuggestionCandidate[] = [];
  const lowerPrefix = prefix.toLowerCase();

  for (const { id, artifact } of artifacts) {
    const props = artifact.properties ?? {};
    const patterns = artifact.files?.path_patterns ?? [];

    // Artifact-level candidate
    if (!prefix || id.toLowerCase().includes(lowerPrefix)) {
      candidates.push({
        label: id,
        path: patterns.length > 0 ? String(patterns[0]) : id,
        kind: "artifact",
        authority: props.authority as string | undefined,
        manual_edit: props.manual_edit as string | undefined,
        score: computeArtifactScore(id, artifact, prefix),
      });
    }

    // File-level candidates from path patterns
    for (const pattern of patterns) {
      if (typeof pattern !== "string") continue;
      // Skip pure glob patterns for file-level suggestions (they're covered by artifact-level)
      if (pattern.includes("*") || pattern.includes("?")) continue;

      if (!prefix || pattern.toLowerCase().includes(lowerPrefix)) {
        candidates.push({
          label: pattern,
          path: pattern,
          kind: "file",
          artifact: id,
          authority: props.authority as string | undefined,
          manual_edit: props.manual_edit as string | undefined,
          score: computeFileScore(pattern, prefix),
        });
      }
    }
  }

  // Sort by score descending, then by label
  candidates.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.label.localeCompare(b.label);
  });

  return candidates.slice(0, maxCandidates);
}

/**
 * Format candidates as NDJSON (one JSON object per line).
 */
export function formatNdjson(candidates: FileSuggestionCandidate[]): string {
  return candidates.map((c) => JSON.stringify(c)).join("\n");
}

// ---------------------------------------------------------------------------
// Scoring helpers
// ---------------------------------------------------------------------------

function computeArtifactScore(
  id: string,
  artifact: NavigationArtifact,
  prefix: string,
): number {
  let score = 50;
  const props = artifact.properties ?? {};

  // Boost canonical, editable artifacts
  if (props.authority === "canonical") score += 20;
  if (props.manual_edit === "allowed") score += 10;

  // Boost source artifacts
  if (props.type === "source") score += 15;

  // Penalize generated artifacts
  if (props.authority === "generated") score -= 20;
  if (props.manual_edit === "forbidden") score -= 10;

  // Boost exact prefix match
  if (prefix && id.toLowerCase().startsWith(prefix.toLowerCase())) {
    score += 20;
  }

  return score;
}

function computeFileScore(path: string, prefix: string): number {
  let score = 40;

  // Boost exact prefix match
  if (prefix && path.toLowerCase().startsWith(prefix.toLowerCase())) {
    score += 30;
  }

  // Boost shorter paths (more specific)
  score += Math.max(0, 20 - path.split("/").length * 2);

  return score;
}

/**
 * Main handler for file-suggestion bin script.
 * Reads prefix from stdin, returns NDJSON candidates to stdout.
 */
export async function handleFileSuggestion(
  navigationIndex: Record<string, unknown>,
  stdinData: string,
): Promise<string> {
  const prefix = stdinData.trim();
  const candidates = generateCandidates(prefix, { navigationIndex });
  return formatNdjson(candidates);
}
